// src/components/common/Analytics.js
// Integracion con Google Analytics 4 (GA4).
//
// Como funciona:
//   1. Al montarse, carga el script gtag.js una sola vez desde googletagmanager.com.
//   2. Configura la propiedad con anonymize_ip y SameSite=None;Secure (privacy-friendly).
//   3. Envia un pageview cada vez que cambia la ruta (SPA-aware).
//
// Config:
//   Requiere REACT_APP_GA_MEASUREMENT_ID en .env local y en Vercel. Formato:
//     REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
//   Si no esta seteada o vale un placeholder, el componente no hace nada
//   (perfecto para dev/preview sin contaminar metricas).
//
// Como conseguir el ID:
//   1. https://analytics.google.com → Admin (icono engranaje) → Create Property
//   2. Nombre: "AVlink Network", timezone Argentina/Buenos Aires, moneda USD
//   3. Data Streams → Add Stream → Web → URL: https://www.avlinknetwork.org
//   4. Copiar el "Measurement ID" (empieza con G-)
//   5. Pegarlo en .env local y en Vercel (Production, Preview, Development)
//   6. Redeploy
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// El scaffold viejo tenia G-XXXXXXXXXX hardcodeado; lo trato como "no seteado".
const isValidId = (id) => !!id && /^G-[A-Z0-9]{6,}$/.test(id) && id !== 'G-XXXXXXXXXX';

const Analytics = () => {
  const location = useLocation();

  // 1) Cargar gtag.js una unica vez
  useEffect(() => {
    if (!isValidId(GA_ID)) return;
    if (window.__ga_loaded) return;
    window.__ga_loaded = true;

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
      cookie_expires: 365 * 24 * 60 * 60,
      // Nosotros mandamos los pageviews manualmente en el useEffect de abajo
      // (para el flow SPA de React Router). Evitamos que gtag mande uno automatico
      // en cada config().
      send_page_view: false,
    });
  }, []);

  // 2) Trackear cambios de ruta (SPA pageview)
  useEffect(() => {
    if (!isValidId(GA_ID) || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
};

export default Analytics;

// ─────────────────────────────────────────────────────────────
// Helpers para trackear eventos custom desde cualquier componente
// ─────────────────────────────────────────────────────────────

/**
 * Loguea un evento generico a GA4.
 * Ejemplo:
 *   trackEvent('signup', { method: 'google', user_type: 'individual' });
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isValidId(GA_ID) || !window.gtag) return;
  window.gtag('event', eventName, params);
};

/**
 * Atajos para eventos comunes de AVlink Network.
 * Extenden los eventos "recomendados" de GA4 (sign_up, login) con params de negocio.
 */
export const trackSignup = ({ method = 'email', userType = null } = {}) => {
  trackEvent('sign_up', { method, user_type: userType });
};

export const trackLogin = ({ method = 'email' } = {}) => {
  trackEvent('login', { method });
};

export const trackProfileCompleted = ({ userType }) => {
  trackEvent('profile_completed', { user_type: userType });
};

export const trackOutreachRegistration = ({ campaign, userType }) => {
  trackEvent('outreach_conversion', { campaign, user_type: userType });
};
